/** Preset options for dashboard "Show last" selectors. */
export const DASHBOARD_DAY_RANGE_OPTIONS = [
  { value: "today", label: "Today", days: 1 },
  { value: "yesterday", label: "Yesterday" },
  { value: "3", label: "3 day", days: 3 },
  { value: "7", label: "7 day", days: 7 },
  { value: "30", label: "30 days", days: 30 },
  { value: "last_month", label: "Last month" },
  { value: "custom", label: "Custom date" },
];

/**
 * Human-readable label for a day-count option (e.g. "1 Day", "7 Days").
 * Kept for any callers that still pass a numeric day count.
 */
export const formatDashboardDayRangeLabel = (days) =>
  days === 1 ? "1 Day" : `${days} Days`;

const INDIA_UTC_OFFSET_MINUTES = 330;

// Build an instant from Asia/Kolkata calendar parts. Using Date.UTC directly here
// would describe a UTC calendar day, which ends 5.5 hours into the next IST day.
const buildIndiaDate = (year, month, day, hour, minute, second, millisecond) =>
  new Date(
    Date.UTC(year, month, day, hour, minute, second, millisecond) -
      INDIA_UTC_OFFSET_MINUTES * 60 * 1000
  );

/**
 * Builds an API payload for a rolling day range ending today (local calendar day).
 *
 * @param {number} days - Number of calendar days including today (1 = today only).
 * @param {Date} [referenceDate=new Date()] - Anchor date treated as "today".
 * @returns {{ days: number, startDate: string, endDate: string }}
 */
export const buildDashboardDaysDateRange = (days, referenceDate = new Date()) => {
  const ref = new Date(referenceDate);
  const year = ref.getFullYear();
  const month = ref.getMonth();
  const date = ref.getDate();

  const end = buildIndiaDate(year, month, date, 23, 59, 59, 999);
  const start = buildIndiaDate(year, month, date - (days - 1), 0, 0, 0, 0);

  return {
    days,
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
};

/**
 * Builds start/end ISO range for a local calendar day (inclusive).
 */
const buildLocalDayUtcRange = (year, month, day) => {
  const start = buildIndiaDate(year, month, day, 0, 0, 0, 0);
  const end = buildIndiaDate(year, month, day, 23, 59, 59, 999);
  return {
    days: 1,
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
};

/**
 * Builds API payload for a "Show last" filter preset.
 * Day-based presets reuse buildDashboardDaysDateRange so calculation logic stays the same.
 *
 * @param {string} filter - One of DASHBOARD_DAY_RANGE_OPTIONS values.
 * @param {{ customStartDate?: Date|null, customEndDate?: Date|null, referenceDate?: Date }} [options]
 * @returns {{ days: number, startDate: string, endDate: string } | null}
 */
export const buildDashboardFilterDateRange = (
  filter,
  { customStartDate = null, customEndDate = null, referenceDate = new Date() } = {}
) => {
  const ref = new Date(referenceDate);
  const year = ref.getFullYear();
  const month = ref.getMonth();
  const date = ref.getDate();

  const preset = DASHBOARD_DAY_RANGE_OPTIONS.find((o) => o.value === filter);

  if (preset?.days) {
    return buildDashboardDaysDateRange(preset.days, referenceDate);
  }

  if (filter === "yesterday") {
    return buildLocalDayUtcRange(year, month, date - 1);
  }

  if (filter === "last_month") {
    const start = buildIndiaDate(year, month - 1, 1, 0, 0, 0, 0);
    const end = buildIndiaDate(year, month, 0, 23, 59, 59, 999);
    const days =
      Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    return {
      days,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }

  if (filter === "custom") {
    if (!customStartDate || !customEndDate) return null;
    const startLocal = new Date(customStartDate);
    const endLocal = new Date(customEndDate);
    const start = buildIndiaDate(
      startLocal.getFullYear(), startLocal.getMonth(), startLocal.getDate(), 0, 0, 0, 0
    );
    const end = buildIndiaDate(
      endLocal.getFullYear(), endLocal.getMonth(), endLocal.getDate(), 23, 59, 59, 999
    );
    const days =
      Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    return {
      days,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }

  // Fallback: treat unknown numeric-like values as day counts
  const asNumber = Number(filter);
  if (!Number.isNaN(asNumber) && asNumber > 0) {
    return buildDashboardDaysDateRange(asNumber, referenceDate);
  }

  return buildDashboardDaysDateRange(1, referenceDate);
};

/**
 * Formats a day-range for UI display (local calendar dates).
 * Single-day ranges show one date; multi-day ranges show "start - end".
 */
export const formatDashboardDaysDateRangeLabel = (days, referenceDate = new Date()) => {
  const ref = new Date(referenceDate);
  const start = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() - (days - 1));
  const end = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());

  const startLabel = start.toLocaleDateString();
  const endLabel = end.toLocaleDateString();
  return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
};

/**
 * Formats a filter preset range for UI display.
 */
export const formatDashboardFilterDateRangeLabel = (
  filter,
  { customStartDate = null, customEndDate = null, referenceDate = new Date() } = {}
) => {
  const range = buildDashboardFilterDateRange(filter, {
    customStartDate,
    customEndDate,
    referenceDate,
  });
  if (!range) return "";

  const start = new Date(range.startDate);
  const end = new Date(range.endDate);
  const startLabel = start.toLocaleDateString(undefined, { timeZone: "Asia/Kolkata" });
  const endLabel = end.toLocaleDateString(undefined, { timeZone: "Asia/Kolkata" });

  return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
};
